import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../../lib/api";
import { normalizeName } from "../../lib/finance";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { Check, ChevronDown, Link2, Loader2, Plus, X } from "lucide-react";

// Supplier of an expense or a subscription. With the purchases module on, the choice is a supplier of its referential (`supplier_id`);
// free text stays possible (no sheet yet, small one-off purchase), and whoever manages suppliers may create the sheet from here.
// Without the module, or without the right to read it, the names already used on expenses are offered, as before.
// `onChange({ supplier, supplier_id })`: `supplier_id` is "" for a free-text name.
export default function SupplierCombobox({ value, supplierId = "", onChange, testid = "finance-supplier" }) {
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [registry, setRegistry] = useState(null); // { enabled, items: [{id, name}], can_create }
  const [names, setNames] = useState([]); // free-text names already used, when there is no registry
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: query.trim() || undefined };
        let { data } = await api.get("/finance/suppliers/registry", { params }).catch(() => ({ data: { enabled: false, items: [], can_create: false } }));
        if (!data.enabled) {
          const used = await api.get("/finance/suppliers", { params }).then((r) => r.data).catch(() => []);
          if (!cancelled) setNames(used);
          data = { enabled: false, items: [], can_create: false };
        }
        if (!cancelled) setRegistry(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, query]);

  const typed = query.trim().split(/\s+/).join(" ");
  const registered = registry?.enabled;
  const exactSheet = (registry?.items || []).find((s) => normalizeName(s.name) === normalizeName(typed));
  const exactName = names.find((n) => normalizeName(n) === normalizeName(typed));
  const pick = (supplier, id = "") => { onChange({ supplier, supplier_id: id }); setOpen(false); setQuery(""); };

  const createSheet = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/procurement/suppliers", { name: typed, category: "other" });
      toast.success(`Fiche fournisseur créée : ${data.name}`);
      pick(data.name, data.id);
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de créer la fiche fournisseur."));
    } finally {
      setCreating(false);
    }
  };

  const row = "w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 flex items-center gap-2 min-h-[44px] md:min-h-0";
  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) setQuery(""); }}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testid} className="w-full justify-between font-normal h-10">
            <span className={`truncate ${value ? "text-slate-900" : "text-slate-500"}`}>{value || "Rechercher ou ajouter un fournisseur"}</span>
            <ChevronDown size={14} className="text-slate-400 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent title="Fournisseur" align="start" className="w-[min(22rem,calc(100vw-2rem))] p-2 space-y-2"
          onOpenAutoFocus={(e) => { e.preventDefault(); searchRef.current?.focus({ preventScroll: true }); }}>
          <Input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom du fournisseur" maxLength={180}
            className="text-base md:text-sm" data-testid={`${testid}-search`} aria-label="Rechercher un fournisseur"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (typed) { if (exactSheet) pick(exactSheet.name, exactSheet.id); else if (!registered) pick(exactName || typed); } } }} />
          <div className="max-h-60 overflow-y-auto" role="listbox" aria-label="Fournisseurs">
            {registered && typed && !exactSheet && registry.can_create && (
              <button type="button" role="option" aria-selected="false" onClick={createSheet} disabled={creating} data-testid={`${testid}-create`} className={row}>
                {creating ? <Loader2 size={14} className="animate-spin shrink-0" /> : <Plus size={14} className="text-jamora shrink-0" />}
                <span>Créer la fiche « <strong>{typed}</strong> »</span>
              </button>
            )}
            {typed && !exactSheet && !exactName && (
              <button type="button" role="option" aria-selected="false" onClick={() => pick(typed)} data-testid={`${testid}-add`} className={row}>
                <Plus size={14} className="text-slate-400 shrink-0" />
                <span>{registered ? <>Garder « <strong>{typed}</strong> » en texte libre</> : <>Ajouter « <strong>{typed}</strong> » comme nouveau fournisseur</>}</span>
              </button>
            )}
            {loading && <div className="flex justify-center py-3"><Loader2 className="animate-spin text-slate-400" size={16} /></div>}
            {!loading && registered && registry.items.length === 0 && !typed && (
              <p className="py-3 text-center text-xs text-slate-500">Aucune fiche fournisseur pour l'instant. Tapez un nom pour {registry.can_create ? "créer la fiche" : "le garder en texte libre"}.</p>
            )}
            {!loading && registered && registry.items.map((s) => (
              <button key={s.id} type="button" role="option" aria-selected={s.id === supplierId} onClick={() => pick(s.name, s.id)} data-testid={`${testid}-option`}
                className={`${row} justify-between`}>
                <span className="truncate">{s.name}</span>
                {s.id === supplierId && <Check size={14} className="text-jamora shrink-0" />}
              </button>
            ))}
            {!loading && !registered && names.length === 0 && !typed && <p className="py-3 text-center text-xs text-slate-500">Aucun fournisseur enregistré pour l'instant. Tapez un nom pour l'ajouter.</p>}
            {!loading && !registered && names.map((name) => (
              <button key={name} type="button" role="option" aria-selected={name === value} onClick={() => pick(name)} data-testid={`${testid}-option`}
                className={`${row} justify-between`}>
                <span className="truncate">{name}</span>
                {name === value && <Check size={14} className="text-jamora shrink-0" />}
              </button>
            ))}
          </div>
          {value && (
            <Button type="button" variant="ghost" size="sm" className="w-full text-slate-600" onClick={() => pick("")} data-testid={`${testid}-clear`}>
              <X size={14} className="mr-1.5" /> Retirer le fournisseur
            </Button>
          )}
        </PopoverContent>
      </Popover>
      {value && supplierId && (
        <p className="flex items-center gap-1 text-xs text-slate-500" data-testid={`${testid}-linked`}><Link2 size={12} aria-hidden="true" /> Fiche fournisseur liée</p>
      )}
    </div>
  );
}
